import _ from 'lodash'
import { useTheme } from 'styled-components'
import { date } from '~utils/date'
import { capitalize } from '~utils/text'
import { money } from '~utils/money'
import { FuelTypography, FuelGridItem, FuelGridContainer, FuelDivider, FuelIcon } from '@frete.com/fuel-react'
import * as Styled from './styled'
import { Props } from './interfaces'
import Skeleton from '~components/Skeleton'
import { useDeviceDetect } from '~hooks'
import { Pagination } from '~components'
import { getCustomToken } from '~utils/token'
import { TIMELINE_MARGIN_BREAKPOINTS, ICON_SLUGS_DICT } from './constants'
import dsTokens from '@frete.com/fuel-foundation/fretebras/theme.json'

const TimelineList = ({
  extract = [],
  isLoading,
  handlePageChange,
  useCurrentPage,
  useTotalPages,
  showPagination,
}: Props) => {
  const { spacing } = useTheme()
  const { isDesktop, isMobile } = useDeviceDetect()
  const DISTANCE_TOP = isDesktop ? TIMELINE_MARGIN_BREAKPOINTS.lg : TIMELINE_MARGIN_BREAKPOINTS.tablet
  const getLabelFreightName = (isExtra: boolean) =>
    isExtra ? 'Frete Destaque: Extra' : 'Frete Destaque: Incluso no plano'

  const getLabelTimeLine = (chargedAt: string) => {
    const day = date(chargedAt, `dd 'de'`)
    const month = capitalize(date(chargedAt, 'MMMM'))
    const year = date(chargedAt, `'de' yyyy`)

    const label = `${day} ${month} ${year}`

    return label
  }

  const extractsWithSameDate = extract.map((extract) => ({
    ...extract,
    name: extract.productSlug === 'highlighted-freight' ? getLabelFreightName(extract.isExtra) : extract.name,
    timeline: getLabelTimeLine(extract.chargedAt),
  }))

  const data = _(extractsWithSameDate)
    .groupBy((extract) => extract.timeline)
    .map((value, key) => ({ date: key, records: value }))
    .value()

  const getCurrentIcon = (slug: string, isExtra: boolean): string => {
    if (isExtra && slug === 'highlighted-freight') {
      slug = 'highlithed-freight-extra'
    }

    return ICON_SLUGS_DICT[slug] || ''
  }

  const getMaxHeight = () => document.body.offsetHeight - (isMobile ? TIMELINE_MARGIN_BREAKPOINTS.mobile : DISTANCE_TOP)

  return (
    <Styled.Wrapper
      style={{
        maxHeight: isLoading ? 'unset' : getMaxHeight(),
        overflow: isLoading ? 'unset' : 'auto',
      }}
    >
      {isLoading
        ? Array.from(Array(5).keys()).map(() => (
            <Styled.HistoricConsumptionDataWrapper key={getCustomToken()}>
              <Skeleton $width="152px" $height="14px" $margin="7px" isLoading={isLoading} />

              <FuelGridContainer
                spacingTop={parseInt(spacing.km6, 10)}
                spacingBottom={parseInt(spacing.km6, 10)}
                style={{ margin: parseInt(spacing.km0, 10), alignItems: 'center' }}
              >
                <Skeleton $width="26px" $height="26px" isLoading={isLoading} />

                <FuelGridItem sm={2} md={4} lg={7} xlg={9}>
                  <Skeleton $height="14px" $width="100%" $margin="6px" isLoading={isLoading} />
                  <Skeleton $width="53px" $height="14px" $margin="6px" isLoading={isLoading} />
                </FuelGridItem>
                <FuelGridItem sm={1} lg={2} xlg={2}>
                  <Skeleton $width="100%" $height="14px" isLoading={isLoading} />
                </FuelGridItem>
              </FuelGridContainer>
            </Styled.HistoricConsumptionDataWrapper>
          ))
        : data.map((extract, dIndex) => (
            <Styled.HistoricConsumptionDataWrapper key={getCustomToken()}>
              <FuelTypography
                size="body-md"
                weight="bold"
                style={{
                  marginBottom: parseInt(spacing.km4, 10),
                  marginTop: dIndex === 0 ? 0 : parseInt(spacing.km4, 10),
                }}
              >
                {extract.date}
              </FuelTypography>

              {extract.records.map((record) => (
                <FuelGridContainer
                  key={getCustomToken()}
                  spacingTop={parseInt(spacing.km6, 10)}
                  spacingBottom={parseInt(spacing.km6, 10)}
                  style={{ margin: parseInt(spacing.km0, 10), alignItems: 'center' }}
                >
                  <FuelGridItem sm={1} md={1} lg={1} xlg={1}>
                    <div style={{ color: dsTokens.color.icon.neutral.default }}>
                      <FuelIcon name={getCurrentIcon(record.productSlug, record.isExtra)} size="medium" />
                    </div>
                  </FuelGridItem>

                  <FuelGridItem sm={2} md={4} lg={7} xlg={9}>
                    <FuelTypography size="body-sm" weight="regular">
                      {record.name}
                    </FuelTypography>

                    <FuelTypography size="caption" weight="regular" color="neutral-default">
                      {date(record?.chargedAt, 'HH:mm')}
                    </FuelTypography>
                  </FuelGridItem>
                  <FuelGridItem sm={1} lg={2} xlg={2}>
                    <FuelTypography size="body-sm" weight="regular">
                      {money(record.price)}
                    </FuelTypography>
                  </FuelGridItem>
                </FuelGridContainer>
              ))}

              {dIndex !== data?.length - 1 && (
                <div style={{ margin: `${spacing.km1} ${spacing.km0}` }}>
                  <FuelDivider orientation="horizontal" />
                </div>
              )}
            </Styled.HistoricConsumptionDataWrapper>
          ))}

      {showPagination && (
        <Pagination currentPage={useCurrentPage} totalPages={useTotalPages} onPageChange={handlePageChange} noMargin />
      )}
    </Styled.Wrapper>
  )
}

export default TimelineList
